import { Input } from '@ohear/ui'
/**
 * Radio group for actor union status, with a conditional "Member ID"
 * field that only appears when the user is a member of any union (i.e.
 * not 'non_union' and not unset).
 *
 * Options come from the 0009 migration's CHECK constraint:
 *   non_union | sag_aftra | actra | equity | other
 */

export type UnionStatus = 'non_union' | 'sag_aftra' | 'actra' | 'equity' | 'other'

export interface UnionOption {
  value: UnionStatus
  label: string
}

const DEFAULT_OPTIONS: readonly UnionOption[] = [
  { value: 'non_union', label: 'Non-union' },
  { value: 'sag_aftra', label: 'SAG-AFTRA' },
  { value: 'actra', label: 'ACTRA' },
  { value: 'equity', label: 'Equity' },
  { value: 'other', label: 'Other' },
] as const

export interface UnionStatusGroupProps {
  status: UnionStatus | null
  unionId: string | null
  onStatusChange: (next: UnionStatus | null) => void
  onUnionIdChange: (next: string) => void
  options?: readonly UnionOption[]
  disabled?: boolean
  /** Helper text shown under the conditional ID field. */
  unionIdHint?: string
}

export function UnionStatusGroup(props: UnionStatusGroupProps) {
  const {
    status,
    unionId,
    onStatusChange,
    onUnionIdChange,
    options = DEFAULT_OPTIONS,
    disabled,
    unionIdHint,
  } = props

  const showIdField = status && status !== 'non_union'

  return (
    <div className="flex flex-col gap-2">
      <div role="radiogroup" aria-label="Union status" className="flex flex-wrap gap-2">
        {options.map((o) => {
          const checked = status === o.value
          return (
            <label
              key={o.value}
              className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-[13px] cursor-pointer ${
                checked
                  ? 'border-[var(--accent,#3b82f6)] bg-[var(--accent-soft,#eef4ff)]'
                  : 'border-[var(--border)]'
              }`}
            >
              <input
                type="radio"
                name="union_status"
                value={o.value}
                checked={checked}
                onChange={() => onStatusChange(o.value)}
                disabled={disabled}
                className="sr-only"
              />
              {o.label}
            </label>
          )
        })}
      </div>
      {showIdField ? (
        <div className="flex flex-col gap-1">
          <label htmlFor="union-id-input" className="text-[12px] font-medium text-[var(--text)]">
            Union member ID
          </label>
          <Input
            id="union-id-input"
            value={unionId ?? ''}
            onChange={(e) => onUnionIdChange(e.target.value)}
            disabled={disabled}
            placeholder="e.g. SAG-12345"
            autoComplete="off"
          />
          {unionIdHint ? (
            <p className="text-[12px] text-[var(--text-2)] m-0">{unionIdHint}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
